import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  useColorModeValue,
  Card,
  CardBody,
  Divider,
  IconButton,
  Tooltip,
  Flex,
  Skeleton,
  Button,
  Select,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  NumberInput,
  NumberInputField,
  Stack,
  useBreakpoint,
} from '@chakra-ui/react';
import { CopyIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { WalletData, Token } from '../../types/wallet';
import { formatAddress, formatUSD } from '../../utils/format';
import { TokenCard, CurrencyFilterInput } from './components';
import { useWallet } from '../../contexts/WalletContext';
import styles from './styles.module.css';
import { Typography } from '@/kit/antipad-ui/Typography';
import { Button as ButtonKit } from '@/kit/antipad-ui/Button';
import { Flex as FlexKit } from '@/kit/antipad-ui/Flex';

// Тип стратегии покупки
type PurchaseStrategy = 'full' | 'proportional' | 'equal';

interface WalletPortfolioProps extends WalletData {
  isLoading?: boolean;
  isCopying?: boolean;
  solBalance: number | null;
  solUsdBalance: number | null;
  onCopyAddress: () => void;
  onCopyPortfolio: (calculatedAmounts: Record<string, { usd: number; tokens: number }>, purchaseStrategy: 'full' | 'proportional' | 'equal', investmentAmount: number, selectedTokens: Token[]) => void;
  onToggleToken: (symbol: string) => void;
}

export const WalletPortfolio: React.FC<WalletPortfolioProps> = ({
  address,
  balance,
  tokens,
  solBalance,
  solUsdBalance,
  isLoading = false,
  isCopying = false,
  onCopyAddress,
  onCopyPortfolio,
  onToggleToken,
}) => {
  const screen = useBreakpoint();
  const isMobile = screen === 'base' || screen === 'sm';
  const [minValue, setMinValue] = useState(10);
  const [purchaseStrategy, setPurchaseStrategy] = useState<PurchaseStrategy>('full');
  const { publicKey, balance: walletBalance } = useWallet();

  // Сбрасываем стратегию при изменении адреса
  useEffect(() => {
    setPurchaseStrategy('full');
  }, [address]);

  // Используем баланс из контекста кошелька, если подключен, иначе 1000
  const maxInvestmentAmount = publicKey ? walletBalance : 1000;
  const [investmentAmount, setInvestmentAmount] = useState(maxInvestmentAmount);

  // Определяем шаг в зависимости от максимального значения
  const step = maxInvestmentAmount < 50 ? 1 : 10;

  // Обновляем investmentAmount при изменении maxInvestmentAmount
  useEffect(() => {
    setInvestmentAmount(maxInvestmentAmount);
  }, [maxInvestmentAmount]);

  const filteredTokens = useMemo(() =>
    tokens.filter(token => token.usdValue >= minValue),
    [tokens, minValue]
  );

  const selectedTokens = useMemo(() =>
    filteredTokens.filter(token => token.selected),
    [filteredTokens]
  );

  const selectedBalance = useMemo(() =>
    selectedTokens.reduce((acc, token) => acc + token.usdValue, 0),
    [selectedTokens]
  );

  const formattedAddress = useMemo(() => formatAddress(address), [address]);

  const displayedTokens = [...filteredTokens].sort((a, b) => b.usdValue - a.usdValue);

  // Рассчитываем значения для каждого токена в зависимости от стратегии
  const calculatedAmounts = useMemo(() => {
    if (purchaseStrategy === 'full' || !selectedTokens.length) {
      return {};
    }

    const result: Record<string, { usd: number; tokens: number }> = {};

    if (purchaseStrategy === 'equal') {
      // Делим сумму поровну между всеми выбранными токенами
      const amountPerToken = investmentAmount / selectedTokens.length;

      selectedTokens.forEach(token => {
        const tokenPrice = token.usdValue / token.amount; // Цена за один токен
        result[token.symbol] = {
          usd: amountPerToken,
          tokens: amountPerToken / tokenPrice
        };
      });
    } else if (purchaseStrategy === 'proportional') {
      // Сохраняем те же пропорции, что и в текущем портфолио
      const totalSelectedValue = selectedTokens.reduce((sum, token) => sum + token.usdValue, 0);

      selectedTokens.forEach(token => {
        const proportion = token.usdValue / totalSelectedValue;
        const usdAmount = investmentAmount * proportion;
        const tokenPrice = token.usdValue / token.amount; // Цена за один токен

        result[token.symbol] = {
          usd: usdAmount,
          tokens: usdAmount / tokenPrice
        };
      });
    }

    return result;
  }, [purchaseStrategy, selectedTokens, investmentAmount]);

  const handleMinValueChange = (value: number) => {
    setMinValue(value);
    onToggleToken('__reset_filter__');
  };

  const handleStrategyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPurchaseStrategy(e.target.value as PurchaseStrategy);
  };

  const handleInvestmentChange = (value: number) => {
    // Округляем до 2 знаков после запятой в меньшую сторону
    const roundedValue = Math.floor(value * 100) / 100;
    setInvestmentAmount(roundedValue);
  };

  return (
    <VStack spacing={6} width="100%" align="stretch">
      {/* <Card bg={bgColor} borderColor={borderColor} borderWidth="1px"> */}
      <div className={styles.well}>
        {/* <CardBody> */}
        <VStack align="stretch" spacing={4}>
          <Flex justify="space-between" align="center" wrap={isMobile ? 'wrap' : 'nowrap'}>
            <Typography variant={isMobile ? 'large_heading' : '3xLarge_heading'}>Wallet Address</Typography>
            <HStack wrap={'wrap'}>
              {isLoading ? (
                <Skeleton height="24px" width="250px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
              ) : (
                <Typography variant={isMobile ? 'small' : 'regular'} color="var(--color-content-secondary)">
                  {isMobile ? formatAddress(address) : address}
                </Typography>
              )}
              <Tooltip label="Copy address">
                <IconButton
                  aria-label="Copy address"
                  icon={<CopyIcon color="var(--color-content-secondary)" />}
                  size="sm"
                  variant="ghost"
                  onClick={onCopyAddress}
                  isDisabled={isLoading}
                />
              </Tooltip>
              <Tooltip label="View on Solscan">
                <IconButton
                  aria-label="View on Solscan"
                  icon={<ExternalLinkIcon color="var(--color-content-secondary)" />}
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                  href={`https://solscan.io/account/${address}`}
                  as="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  isDisabled={isLoading}
                />
              </Tooltip>
            </HStack>
          </Flex>
          <Divider />
          <Box py={2}>
            {isLoading ? (
              <VStack align="stretch" spacing={3}>
                <Skeleton height="24px" width="250px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
                <Skeleton height="24px" width="220px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
              </VStack>
            ) : (
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Typography color="var(--color-content-tertiary)">SOL Balance</Typography>
                  <Typography color="var(--color-content-secondary)">
                    {solBalance?.toFixed(2)} SOL {' '}
                    <Typography as="span" color="var(--color-content-tertiary)">
                      (${solUsdBalance?.toFixed(2)})
                    </Typography>
                  </Typography>
                </HStack>
                <HStack justify="space-between">
                  <Typography color="var(--color-content-tertiary)">Token Balance</Typography>
                  <Typography color="var(--color-content-secondary)">
                    {tokens.length} Tokens {' '}
                    <Typography as="span" color="var(--color-content-tertiary)" >
                      ({formatUSD(tokens.reduce((acc, token) => acc + token.usdValue, 0))})
                    </Typography>
                  </Typography>
                </HStack>
              </VStack>
            )}
          </Box>
        </VStack>
        {/* </CardBody> */}
      </div>
      {/* </Card> */}

      <div className={styles.well}>
        {/* <Card bg={bgColor} borderColor={borderColor} borderWidth="1px">
          <CardBody> */}
        <VStack align="stretch" spacing={4}>
          <Flex justify="space-between" align="center">
            <Typography variant={isMobile ? 'large_heading' : '3xLarge_heading'}>Portfolio</Typography>
            {isLoading ? (
              <Skeleton height="48px" width="200px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
            ) : (
              <ButtonKit onClick={(e) => {
                e.preventDefault();
                // Получаем только выбранные токены из тех, что отображаются
                const selectedTokens = displayedTokens.filter(token => token.selected);
                onCopyPortfolio(calculatedAmounts, purchaseStrategy, investmentAmount, selectedTokens);
              }} intent='brand'>
                {isCopying ? 'Copying...' : 'Copy Value'}
                {!isMobile && <Box as="span" ml={2} px={3} py={1} bg="rgba(0,0,0,0.15)" borderRadius="3xl" fontWeight="medium">
                  <Typography color='var(--color-content-primary-inverted)'>
                    {purchaseStrategy === 'full' ? formatUSD(selectedBalance) : formatUSD(investmentAmount)}
                  </Typography>
                </Box>}
              </ButtonKit>
            )}
          </Flex>
          <Divider />

          <HStack spacing={4} align="center" wrap={isMobile ? 'wrap' : 'nowrap'}>
            {isLoading ? (
              <Skeleton height="48px" width="210px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
            ) : (
              <Select
                value={purchaseStrategy}
                onChange={handleStrategyChange}
                // size="sm"
                order={isMobile ? 1 : 0}
                maxW="310px"
                bg="var(--color-background-neutral-subtle)"
                style={{ font: 'var(--typo-body-regular)', outline: 'none', border: 'none', color: 'var(--color-content-primary)', backgroundColor: 'var(--color-background-neutral-subtle)' }}
                color="var(--color-content-primary)"
                borderColor="transparent"
                borderRadius="3xl"
                icon={
                  <Box
                    as="span"
                    fontSize="10px"
                    color="gray.400"
                    mr={2}
                  >
                    ▾
                  </Box>
                }
              >
                <option value="full" style={{ background: '#1A202C', }}>
                  Original Buy
                </option>
                <option value="proportional" style={{ background: '#1A202C' }}>Initial Ratio Buy</option>
                <option value="equal" style={{ background: '#1A202C' }}>Spray Buy</option>
              </Select>
            )}
            {isLoading ? (
              <Skeleton height="48px" width="210px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
            ) : (
              <CurrencyFilterInput
                initialValue={minValue}
                onChange={handleMinValueChange}
              />
            )}
          </HStack>

          {/* Investment amount slider - показываем только если не выбран full */}
          {purchaseStrategy !== 'full' && (
            <div style={{ backgroundColor: '#212121' }} className={styles.well}>
              <VStack spacing={3} width="100%">
                <FlexKit justify="space-between" align="center" gap={isMobile ? 'm' : '0'} wrap={isMobile} fullWidth>
                  <Typography color='var(--color-content-secondary)'>Investment amount</Typography>
                  {isLoading ? (
                    <Skeleton height="32px" width="100px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
                  ) : (
                    <NumberInput
                      value={Math.floor(investmentAmount * 100) / 100}
                      onChange={(_, val) => handleInvestmentChange(val)}
                      min={1}
                      max={maxInvestmentAmount}
                      precision={2}
                      step={step}
                      border="none"
                    // size="sm"
                    >
                      <NumberInputField
                        textAlign="right"
                        border="none"
                        color="white"
                        style={{ font: 'var(--typo-body-regular)', outline: 'none', border: 'none' }}
                        bg="var(--color-background-neutral-subtle)"
                        borderRadius="3xl"
                        pr={3}
                        _focus={{ boxShadow: "none" }}
                        _before={{
                          content: '"$"',
                          position: "absolute",
                          left: "0.5rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "gray.400",
                          pointerEvents: "none"
                        }}
                      />
                    </NumberInput>
                  )}
                </FlexKit>

                {isLoading ? (
                  <Skeleton height="4px" width="100%" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
                ) : (
                  <Flex width="100%" align="center" gap={2}>
                    <Typography color='var(--color-content-secondary)'>$1</Typography>
                    <Slider
                      aria-label="investment-amount"
                      value={Math.floor(investmentAmount * 100) / 100}
                      onChange={handleInvestmentChange}
                      min={1}
                      max={maxInvestmentAmount}
                      step={step}
                      focusThumbOnChange={false}
                      mx={3}
                      colorScheme="blue"
                    >
                      <SliderTrack bg="gray.700" height="3px">
                        <SliderFilledTrack bg="#44FFA8" />
                      </SliderTrack>
                      <SliderThumb
                        boxSize={4}
                        bg="white"
                        borderColor="gray.300"
                        borderWidth="1px"
                        _focus={{
                          boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.4)"
                        }}
                      />
                    </Slider>
                    <Typography color='var(--color-content-secondary)'>${Math.floor(maxInvestmentAmount * 100) / 100}</Typography>
                  </Flex>
                )}
              </VStack>
            </div>
          )}

          <VStack align="stretch" spacing={3}>
            {isLoading ? (
              Array(3).fill(0).map((_, i) => (
                <Box key={i} p={4} bg="#131313" borderRadius="3xl" borderWidth="1px" borderColor="var(--color-border-neutral-subtle)">
                  <Flex justify="space-between" align="center">
                    <HStack spacing={4}>
                      <Skeleton height="40px" width="40px" borderRadius="full" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
                      <Stack spacing={2}>
                        <Skeleton height="20px" width="120px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
                        <Skeleton height="16px" width="80px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
                      </Stack>
                    </HStack>
                    <HStack spacing={4}>
                      <Stack spacing={2} align="flex-end">
                        <Skeleton height="20px" width="100px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
                        <Skeleton height="16px" width="80px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
                      </Stack>
                      <Skeleton height="24px" width="48px" borderRadius="3xl" startColor="var(--color-background-neutral-subtle)" endColor="var(--color-background-neutral-canvas-subtle)" />
                    </HStack>
                  </Flex>
                </Box>
              ))
            ) : displayedTokens.map((token, index) => (
              <TokenCard
                key={`${token.symbol}-${index}`}
                token={token}
                onToggle={() => onToggleToken(token.mint)}
                purchaseStrategy={purchaseStrategy}
                calculatedAmount={calculatedAmounts[token.symbol]}
              />
            ))}
          </VStack>
        </VStack>
        {/* </CardBody>
        </Card> */}
      </div >
    </VStack >
  );
}; 